import { actionEmitter, EmitEvent } from "../config/ActionEmitter";
import { IApiResponse, ISiteMenuGroup, ISiteMenuItem, ISiteMenuTree } from "../models/Interfaces";
import { BaseService } from "./BaseService";

export class SiteMenuService extends BaseService {

  public getSiteMenusForLanguage = async (lang: string) : Promise<Array<ISiteMenuTree> | null> => {
    if (this.siteKey && lang) {
      const url: string = `/SiteAdmin/GetMenuHierarchy/${this.siteKey}/${lang}`;
      const res = await this._ajaxService.Get<Array<ISiteMenuTree> | null>(url, true);
      return res;
    }
    return null;
  }

  public getSiteMenuGroup = async (groupid: string) : Promise<ISiteMenuGroup | null> => {
    if (this.siteKey && groupid) {
      const url: string = `/SiteAdmin/GetMenuGroup/${this.siteKey}/${groupid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Unable to retrieve menu group details");
      }
    }
    return null;
  }

  public getSiteMenuItem = async (itemid: string) : Promise<ISiteMenuItem | null> => {
    if (this.siteKey && itemid) {
      const url: string = `/SiteAdmin/GetMenuItem/${this.siteKey}/${itemid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Unable to retrieve menu item details");
      }
    }
    return null;
  }

  public saveMenuGroup = async (group: ISiteMenuGroup) : Promise<ISiteMenuGroup | null> => {
    if (this.siteKey && group) {
      const url: string = `/SiteAdmin/AddUpdateMenuGroup`;
      // const cmd = new AddUpdateMenuGroupCommand();

      // cmd.MenuGroupId = group.MenuGroupId;
      // //cmd.Alignment = 
      group.SiteId = this.siteKey;
      const res = await this._ajaxService.Post<IApiResponse>(url, group, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Unable to save menu group");
      }
    }
    return null;
  }

  public deleteMenuGroup = async (groupid: string) : Promise<boolean> => {
    if (this.siteKey && groupid) {
      const url: string = `/SiteAdmin/DeleteMenuGroup/${this.siteKey}/${groupid}`;

      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Unable to remove selected menu group. Delete Failed.");
      }
    }
    return false;
  };

  public saveMenuItem = async (menuitem: ISiteMenuItem) : Promise<ISiteMenuItem | null> => {
    if (this.siteKey && menuitem) {
      const url: string = `/SiteAdmin/AddUpdateMenu`;
      menuitem.SiteId = this.siteKey;

      const saveres = await this._ajaxService.Post<IApiResponse>(url, menuitem, true);

      if (saveres?.StatusCode === 200) {
        return saveres.ReturnData ?? menuitem;
      } else {
        this.EmitDataError(saveres, "Failed to save menu item");
      }
    }
    return null;
  };

  public deleteMenuItem = async (itemid: number) : Promise<boolean> => {
    if (this.siteKey && itemid && itemid > 0) {
      const url = `/SiteAdmin/DeleteMenu/${this.siteKey}/${itemid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Unable to remove selected menu item. Delete Failed.");
      }
    }
    return false;
  };

  public menuToTreeItem = (item: ISiteMenuGroup | ISiteMenuItem) : ISiteMenuTree | null => {
    let ti: ISiteMenuTree | null = null;
    if (item.Type === "menugroup") {
      const gi = item as ISiteMenuGroup;
      ti = {
        id: gi.MenuGroupId,
        text: gi.Name,
        language: gi.Language,
        children: [],
        displayorder: 0,
        parent: "#",
        type: gi.Type,
        menugroupid: gi.MenuGroupId
      };
    } else {
      const mi = item as ISiteMenuItem;
      ti = {
        id: mi.Id.toString(),
        children: null,
        displayorder: mi.DisplayOrder,
        text: mi.DisplayText,
        language: mi.Language,
        menugroupid: mi.MenuGroupId ?? "",
        parent: mi.ParentId === 0 ? mi.MenuGroupId : mi.ParentId?.toString() ?? "",
        type: mi.Type
      };
    }
    return ti;
  };

  public newMenuGroup = (lang: string) : ISiteMenuGroup => {
    return {
      SiteId: this.siteKey ?? "",
      Alignment: "",
      BackgroundColour: "",
      Enabled: true,
      Height: "",
      HighlightColour: "",
      IsItemGroup: true,
      KeepAtTop: false,
      Language: lang,
      Logo: "",
      MenuGroupId: "",
      MenuItems: [],
      Name: "",
      Shadow: false,
      TextColour: "",
      Type: "menugroup"
    }
  }

  public newMenuItem = (language: string, parentid?: number, groupid?: string) : ISiteMenuItem => {
    return {
      Id: 0,
      BackgroundColour: "",
      Children: [],
      DisplayOrder: 0,
      ParentId: parentid,
      MenuGroupId: groupid,
      DisplayText: "",
      Enabled: true,
      HeaderColAlign: "",
      HighlightColour: "",
      Language: language,
      MenuItemClass: "",
      Order: 0,
      StyledColumn: "",
      Target: "",
      TextColour: "",
      Type: "menuitem",
      Url: "",
      Parent: null,
      SiteId: this.siteKey ?? ""
    };
  }

  // public treeNodeToMenuItem = (treenode: IArboristTreeNode) : ISiteMenuItem => {
  //   return {
  //     Id: treenode.id,
  //     BackgroundColour: treenode.
  //   }
  // }

}